import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import {
  and,
  asc,
  between,
  count,
  desc,
  eq,
  exists,
  gt,
  gte,
  inArray,
  like,
  lt,
  lte,
  notInArray,
  or,
  sql,
} from "drizzle-orm";
import z from "zod";
import { db } from "~/db";
import { auth } from "~/lib/better-auth/auth";
import { locationsToUsers } from "~/db/schema";
import { parseBilingual } from "~/lib/utils/parse-bilingual";
import {
  eventType,
  file,
  infraType,
  location,
  locationContact,
  locationInfrastructure,
  locationOpeningHour,
  locationRoom,
  locationTechnology,
  locationsToEventTypes,
  locationsToFiles,
  locationsToInfraTypes,
  locationsToLocationTypes,
  locationType,
  roomShape,
  additionalAttribute,
  additionalContractBinding,
  user,
} from "~/db/schema";
import { fileCategory } from "~/db/schema/localground/fileCategory.schema";
import { filesToCategories } from "~/db/schema/localground/filesToCategories.schema";
import { hotelRoom } from "~/db/schema/localground/hotelRoom.schema";
import { hotelRoomsToFiles } from "~/db/schema/localground/hotelRoomsToFiles.schema";
import { hotelType } from "~/db/schema/localground/hoteltype.schema";
import { locationroomsToFiles } from "~/db/schema/localground/locationroomsToFiles.schema";
import { locationCertificate } from "~/db/schema/localground/locationCertificate.schema";
import { locationsToHotelTypes } from "~/db/schema/localground/locationsToHotelTypes.schema";
import { getStateCodeFromLabel } from "~/lib/add-location/state-options";
import { limitImagesByPlan } from "~/lib/plans/plan-limits";
import { SearchParams, SearchParamsSchema } from "~/types/search";
import { geocodeLocationInternal } from "./geocode-location";
import { getPlaceDetails } from "~/lib/server-functions/get-place-detail";
import type { MapsCallSource } from "~/lib/maps/attribution";
import { outsidearea } from "~/db/schema/localground/outsidearea.schema";
import { outsideareasToFiles } from "~/db/schema/localground/outsideareasToFiles.schema";

export const groupImagesByCategory = (
  images: Array<{
    id: number | null;
    path: string | null;
    mimetype: string | null;
    alt: string | null;
    size: number | null;
    sort: number | null;
    categoryId: number | null;
    categoryName: string | null;
    purpose?: string | null;
    /** Used to order groups (e.g. 0=Location, 1+=rooms, 999=Hotel). Lower first. */
    categorySortOrder?: number | null;
  }>,
  options?: {
    excludeCategoryName?: string[];
    excludePurpose?: string[];
  },
) => {
  const grouped: Record<
    number,
    {
      name: string | null;
      categoryId: number | null;
      categorySortOrder: number;
      images: Array<{
        id: number | null;
        path: string | null;
        mimetype: string | null;
        alt: string | null;
        size: number | null;
        sort: number | null;
      }>;
    }
  > = {};

  for (const row of images) {
    const id = row.categoryId ?? -1;

    if (!row.mimetype || !row.mimetype.startsWith("image")) {
      continue;
    }

    if (
      options?.excludePurpose?.length &&
      row.purpose &&
      options.excludePurpose.includes(row.purpose)
    ) {
      continue;
    }

    if (
      options?.excludeCategoryName?.length &&
      row.categoryName &&
      options.excludeCategoryName.includes(row.categoryName)
    ) {
      continue;
    }

    const sortOrder = row.categorySortOrder ?? Infinity;
    if (!grouped[id]) {
      grouped[id] = {
        name: row.categoryName,
        categoryId: row.categoryId,
        categorySortOrder: sortOrder,
        images: [],
      };
    } else {
      grouped[id].categorySortOrder = Math.min(
        grouped[id].categorySortOrder,
        sortOrder,
      );
    }

    grouped[id].images.push({
      id: row.id,
      path: row.path,
      mimetype: row.mimetype,
      alt: row.alt,
      size: row.size,
      sort: row.sort,
    });
  }

  const sortedGroups = Object.values(grouped).sort((a, b) => {
    const orderA = a.categorySortOrder;
    const orderB = b.categorySortOrder;
    if (orderA !== orderB) return orderA - orderB;
    const aId = a.categoryId ?? -1;
    const bId = b.categoryId ?? -1;
    return aId - bId;
  });

  return sortedGroups;
};

const getLocationWithGeoBoundsSchema = z.object({
  bounds: z
    .object({
      southWest: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
      northEast: z.object({
        lat: z.number(),
        lng: z.number(),
      }),
    })
    .nullable(),
  page: z.number().int().min(1).default(1),
  pageSize: z.number().int().min(1).max(100).default(50),
  searchParams: SearchParamsSchema,
});

export type Image = {
  id: number;
  path: string;
  mimetype: string | null;
  alt: string | null;
  size: number | null;
  sort: number | null;
  categoryId: number | null;
  categoryName: string | null;
  purpose?: string | null;
  /** Order for gallery groups: 0=Location, 1+=rooms, 999=Hotel. */
  categorySortOrder?: number | null;
};

export type LocationWithImages = typeof location.$inferSelect & {
  images: Array<Image>;
  eventTypes: Array<{
    id: number;
    name: string;
  }>;
  locationTypes: Array<{
    id: number;
    name: string;
  }>;
  rooms?: Array<{
    id: number;
    name: string;
  }>;
};

// Helper function to parse and normalize bilingual JSON fields
// Converts optional/nullable fields to required strings (with empty string fallback)
// to match the schema type definition
function parseAndNormalizeBilingual(
  value: unknown,
): { german: string; english: string } | null {
  const parsed = parseBilingual(value);
  if (!parsed) return null;
  return {
    german: parsed.german ?? "",
    english: parsed.english ?? "",
  };
}

export type RoomWithImages = typeof locationRoom.$inferSelect & {
  images: Array<Image>;
  roomShape?: { id: number; name: string } | null;
  additionalAttributes?: Array<{
    id: number;
    name: { german: string; english: string } | null;
    purpose: string;
    active: boolean | null;
  }>;
};

export type OutsideAreaWithImages = typeof outsidearea.$inferSelect & {
  images: Array<Image>;
};

export type HotelRoomWithImages = typeof hotelRoom.$inferSelect & {
  images: Array<Image>;
};

type ResolvedSearchLocation = {
  lat: number;
  lng: number;
  viewport?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
  bounds?: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
};

export const getLocationsWithGeoBounds = createServerFn({ method: "GET" })
  .inputValidator(getLocationWithGeoBoundsSchema)
  .handler(async (ctx) => {
    const { bounds, searchParams, page = 1, pageSize = 50 } = ctx.data;

    const budgetCondition = getBudgetCondition(searchParams);
    const useBudgetSplit = budgetCondition !== null;
    const deviatingBudgetCondition = useBudgetSplit
      ? getDeviatingBudgetCondition(searchParams)
      : null;
    const resolvedLocations: Array<ResolvedSearchLocation | null> = Array.from(
      { length: searchParams.location.length },
      () => null,
    );

    // Base where: all filters except budget (so we can split matching vs deviating when useBudgetSplit)
    const baseWhere: any[] = [
      ...(await getLocationSearchWhereClause(
        searchParams,
        !useBudgetSplit,
        "search_list",
        resolvedLocations,
      )),
    ];

    if (bounds) {
      baseWhere.push(
        between(location.lat, bounds.southWest.lat, bounds.northEast.lat),
        between(location.lng, bounds.southWest.lng, bounds.northEast.lng),
      );
    }

    const where = useBudgetSplit
      ? and(...baseWhere, budgetCondition)
      : and(...baseWhere);

    // Get total count(s)
    let total: number;
    let totalMatching: number | undefined;
    let totalDeviating: number | undefined;

    if (useBudgetSplit) {
      const [matchingCountResult, deviatingCountResult] = await Promise.all([
        db
          .select({ count: sql<number>`count(*)` })
          .from(location)
          .where(and(...baseWhere, budgetCondition))
          .execute(),
        db
          .select({ count: sql<number>`count(*)` })
          .from(location)
          .where(and(...baseWhere, deviatingBudgetCondition))
          .execute(),
      ]);
      totalMatching = matchingCountResult[0]?.count ?? 0;
      totalDeviating = deviatingCountResult[0]?.count ?? 0;
      total = totalMatching + totalDeviating;
    } else {
      const countResult = await db
        .select({ count: sql<number>`count(*)` })
        .from(location)
        .where(where)
        .execute();
      total = countResult[0]?.count ?? 0;
    }

    const offset = (page - 1) * pageSize;

    let order = [
      // Sort by plan first: superior (1) > premium (2) > basic (3)
      sql`CASE 
        WHEN ${location.plan} = 'superior' THEN 1
        WHEN ${location.plan} = 'premium' THEN 2
        WHEN ${location.plan} = 'basic' THEN 3
        ELSE 4
      END ASC`,
      desc(location.updatedAt)
    ];
    if (searchParams.sort === "price-asc") {
      // budget (1) < moderate (2) < upscale (3) < premium (4) < luxury (5)
      order = [
        sql`${location.priceLevel} IS NULL`,
        sql`CASE 
          WHEN ${location.priceLevel} = 'budget' THEN 1
          WHEN ${location.priceLevel} = 'moderate' THEN 2
          WHEN ${location.priceLevel} = 'upscale' THEN 3
          WHEN ${location.priceLevel} = 'premium' THEN 4
          WHEN ${location.priceLevel} = 'luxury' THEN 5
          ELSE 0
        END ASC`,
      ];
    } else if (searchParams.sort === "price-desc") {
      // luxury (5) > premium (4) > upscale (3) > moderate (2) > budget (1)
      order = [
        sql`${location.priceLevel} IS NULL`,
        sql`CASE 
          WHEN ${location.priceLevel} = 'budget' THEN 1
          WHEN ${location.priceLevel} = 'moderate' THEN 2
          WHEN ${location.priceLevel} = 'upscale' THEN 3
          WHEN ${location.priceLevel} = 'premium' THEN 4
          WHEN ${location.priceLevel} = 'luxury' THEN 5
          ELSE 0
        END DESC`,
      ];
    }

    // Facet count where: when budget split, count over all (matching + deviating); otherwise use main where
    const facetWhere = useBudgetSplit ? and(...baseWhere) : where;

    // Calculate counts for event types, location types, and infra types
    const [eventTypeCounts, locationTypeCounts, infraTypeCounts] =
      await Promise.all([
        db
          .select({
            id: eventType.id,
            name: eventType.name,
            locationCount: count(locationsToEventTypes.locationId),
          })
          .from(eventType)
          .leftJoin(
            locationsToEventTypes,
            eq(locationsToEventTypes.eventTypeId, eventType.id),
          )
          .leftJoin(location, eq(location.id, locationsToEventTypes.locationId))
          .where(facetWhere)
          .groupBy(eventType.id, eventType.name)
          .orderBy(asc(eventType.name))
          .execute(),
        db
          .select({
            id: locationType.id,
            name: locationType.name,
            locationCount: count(locationsToLocationTypes.locationId),
          })
          .from(locationType)
          .leftJoin(
            locationsToLocationTypes,
            eq(locationsToLocationTypes.locationTypeId, locationType.id),
          )
          .leftJoin(location, eq(location.id, locationsToLocationTypes.locationId))
          .where(facetWhere)
          .groupBy(locationType.id, locationType.name)
          .orderBy(asc(locationType.name))
          .execute(),
        db
          .select({
            id: infraType.id,
            name: infraType.name,
            locationCount: count(locationsToInfraTypes.locationId),
          })
          .from(infraType)
          .leftJoin(
            locationsToInfraTypes,
            eq(locationsToInfraTypes.infraTypeId, infraType.id),
          )
          .leftJoin(location, eq(location.id, locationsToInfraTypes.locationId))
          .where(facetWhere)
          .groupBy(infraType.id, infraType.name)
          .orderBy(asc(infraType.name))
          .execute(),
      ]);

    // First, get the location IDs we want in the correct order (and budgetMatches when split)
    type IdWithBudget = { id: number; budgetMatches?: boolean };
    let locationIds: IdWithBudget[];

    if (useBudgetSplit) {
      const start = offset;
      const end = offset + pageSize;

      if (end <= totalMatching!) {
        const rows = await db
          .select({ id: location.id })
          .from(location)
          .where(and(...baseWhere, budgetCondition))
          .orderBy(...order)
          .limit(pageSize)
          .offset(start)
          .execute();
        locationIds = rows.map((r) => ({ id: r.id, budgetMatches: true }));
      } else if (start >= totalMatching!) {
        const rows = await db
          .select({ id: location.id })
          .from(location)
          .where(and(...baseWhere, deviatingBudgetCondition))
          .orderBy(...order)
          .limit(pageSize)
          .offset(start - totalMatching!)
          .execute();
        locationIds = rows.map((r) => ({ id: r.id, budgetMatches: false }));
      } else {
        const matchingLimit = totalMatching! - start;
        const [matchingRows, deviatingRows] = await Promise.all([
          db
            .select({ id: location.id })
            .from(location)
            .where(and(...baseWhere, budgetCondition))
            .orderBy(...order)
            .limit(matchingLimit)
            .offset(start)
            .execute(),
          db
            .select({ id: location.id })
            .from(location)
            .where(and(...baseWhere, deviatingBudgetCondition))
            .orderBy(...order)
            .limit(pageSize - matchingLimit)
            .offset(0)
            .execute(),
        ]);
        locationIds = [
          ...matchingRows.map((r) => ({ id: r.id, budgetMatches: true })),
          ...deviatingRows.map((r) => ({ id: r.id, budgetMatches: false })),
        ];
      }
    } else {
      const rows = await db
        .select({ id: location.id })
        .from(location)
        .where(where)
        .orderBy(...order)
        .limit(pageSize)
        .offset(offset)
        .execute();
      locationIds = rows.map((r) => ({ id: r.id }));
    }

    if (locationIds.length === 0) {
      return {
        locations: [],
        total,
        page,
        pageSize,
        totalPages: Math.ceil(total / pageSize),
        primaryLocationResolution: resolvedLocations[0] ?? null,
        ...(useBudgetSplit && {
          totalMatching: totalMatching ?? 0,
          totalDeviating: totalDeviating ?? 0,
        }),
        eventTypeCounts,
        locationTypeCounts,
        infraTypeCounts,
      };
    }

    const ids = locationIds.map((l) => l.id);
    const budgetMatchesById = new Map(
      locationIds
        .filter((l) => l.budgetMatches !== undefined)
        .map((l) => [l.id, l.budgetMatches!]),
    );

    // Fetch locations with images
    const imageRows = await db
      .select({
        location,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
          purpose: locationsToFiles.purpose,
        },
      })
      .from(location)
      .leftJoin(
        locationsToFiles,
        and(
          eq(locationsToFiles.locationId, location.id),
          notInArray(locationsToFiles.purpose, [
            "location_floorplan",
            "location_document",
          ]),
        ),
      )
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(inArray(location.id, ids))
      .orderBy(asc(file.sort))
      .execute();

    // Fetch event types for these locations
    const eventTypeRows = await db
      .select({
        locationId: locationsToEventTypes.locationId,
        eventType: {
          id: eventType.id,
          name: eventType.name,
        },
      })
      .from(locationsToEventTypes)
      .innerJoin(eventType, eq(eventType.id, locationsToEventTypes.eventTypeId))
      .where(inArray(locationsToEventTypes.locationId, ids))
      .orderBy(eventType.name)
      .execute();

    // Fetch location types for these locations
    const locationTypeRows = await db
      .select({
        locationId: locationsToLocationTypes.locationId,
        locationType: {
          id: locationType.id,
          name: locationType.name,
        },
      })
      .from(locationsToLocationTypes)
      .innerJoin(
        locationType,
        eq(locationType.id, locationsToLocationTypes.locationTypeId),
      )
      .where(inArray(locationsToLocationTypes.locationId, ids))
      .orderBy(locationType.name)
      .execute();

    // Build the locations map with all related data
    const locationsMap = new Map<number, LocationWithImages>();

    for (const row of imageRows) {
      if (!locationsMap.has(row.location.id)) {
        locationsMap.set(row.location.id, {
          ...row.location,
          // Parse bilingual JSON fields that might come as strings from the database
          descriptionForm: parseAndNormalizeBilingual(
            row.location.descriptionForm,
          ),
          images: [],
          eventTypes: [],
          locationTypes: [],
        });
      }
      if (row.image?.id) {
        locationsMap.get(row.location.id)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    // Sort images within each location by their sort field and apply plan limits
    for (const location of locationsMap.values()) {
      location.images.sort((a, b) => {
        const sortA = a.sort ?? Number.MAX_SAFE_INTEGER;
        const sortB = b.sort ?? Number.MAX_SAFE_INTEGER;
        return sortA - sortB;
      });
      location.images = limitImagesByPlan(location.images, location.plan);
    }

    // Add event types
    for (const row of eventTypeRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.eventType?.id) {
        loc.eventTypes.push(row.eventType);
      }
    }

    // Add location types
    for (const row of locationTypeRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.locationType?.id) {
        loc.locationTypes.push(row.locationType);
      }
    }

    // Sort locations to match the original sorted order from locationIds; attach budgetMatches when in budget-split mode
    const sortedLocations = ids
      .map((id) => {
        const loc = locationsMap.get(id);
        if (!loc) return undefined;
        if (budgetMatchesById.has(id)) {
          return {
            ...loc,
            budgetMatches: budgetMatchesById.get(id),
          };
        }
        return loc;
      })
      .filter((loc): loc is LocationWithImages & { budgetMatches?: boolean } =>
        loc !== undefined,
      );

    return {
      locations: sortedLocations,
      total,
      page,
      pageSize,
      totalPages: Math.ceil(total / pageSize),
      primaryLocationResolution: resolvedLocations[0] ?? null,
      ...(useBudgetSplit && {
        totalMatching: totalMatching ?? 0,
        totalDeviating: totalDeviating ?? 0,
      }),
      eventTypeCounts,
      locationTypeCounts,
      infraTypeCounts,
    };
  });

export type MarkerCoordinate = {
  id: number;
  lat: number;
  lng: number;
  name: string;
  city: string | null;
  nameForm: { german: string; english: string } | null;
};

const getAllMatchingMarkerCoordinatesSchema = z.object({
  bounds: z
    .object({
      southWest: z.object({ lat: z.number(), lng: z.number() }),
      northEast: z.object({ lat: z.number(), lng: z.number() }),
    })
    .nullable(),
  searchParams: SearchParamsSchema,
});

export const getAllMatchingMarkerCoordinates = createServerFn({ method: "GET" })
  .inputValidator(getAllMatchingMarkerCoordinatesSchema)
  .handler(async (ctx): Promise<MarkerCoordinate[]> => {
    const { bounds, searchParams } = ctx.data;

    const budgetCondition = getBudgetCondition(searchParams);
    const useBudgetSplit = budgetCondition !== null;
    const deviatingBudgetCondition = useBudgetSplit
      ? getDeviatingBudgetCondition(searchParams)
      : null;

    const baseWhere: any[] = [
      ...(await getLocationSearchWhereClause(
        searchParams,
        !useBudgetSplit,
        "search_markers",
      )),
    ];

    if (bounds) {
      baseWhere.push(
        between(location.lat, bounds.southWest.lat, bounds.northEast.lat),
        between(location.lng, bounds.southWest.lng, bounds.northEast.lng),
      );
    }

    let order = [
      sql`CASE 
        WHEN ${location.plan} = 'superior' THEN 1
        WHEN ${location.plan} = 'premium' THEN 2
        WHEN ${location.plan} = 'basic' THEN 3
        ELSE 4
      END ASC`,
      desc(location.updatedAt),
    ];
    if (searchParams.sort === "price-asc") {
      order = [
        sql`${location.priceLevel} IS NULL`,
        sql`CASE 
          WHEN ${location.priceLevel} = 'budget' THEN 1
          WHEN ${location.priceLevel} = 'moderate' THEN 2
          WHEN ${location.priceLevel} = 'upscale' THEN 3
          WHEN ${location.priceLevel} = 'premium' THEN 4
          WHEN ${location.priceLevel} = 'luxury' THEN 5
          ELSE 0
        END ASC`,
      ];
    } else if (searchParams.sort === "price-desc") {
      order = [
        sql`${location.priceLevel} IS NULL`,
        sql`CASE 
          WHEN ${location.priceLevel} = 'budget' THEN 1
          WHEN ${location.priceLevel} = 'moderate' THEN 2
          WHEN ${location.priceLevel} = 'upscale' THEN 3
          WHEN ${location.priceLevel} = 'premium' THEN 4
          WHEN ${location.priceLevel} = 'luxury' THEN 5
          ELSE 0
        END DESC`,
      ];
    }

    const selectFields = {
      id: location.id,
      lat: location.lat,
      lng: location.lng,
      name: location.name,
      city: location.city,
      nameForm: location.nameForm,
    };

    if (useBudgetSplit && deviatingBudgetCondition) {
      const [matchingRows, deviatingRows] = await Promise.all([
        db
          .select(selectFields)
          .from(location)
          .where(and(...baseWhere, budgetCondition))
          .orderBy(...order)
          .execute(),
        db
          .select(selectFields)
          .from(location)
          .where(and(...baseWhere, deviatingBudgetCondition))
          .orderBy(...order)
          .execute(),
      ]);
      return [...matchingRows, ...deviatingRows] as MarkerCoordinate[];
    }

    const rows = await db
      .select(selectFields)
      .from(location)
      .where(and(...baseWhere))
      .orderBy(...order)
      .execute();

    return rows as MarkerCoordinate[];
  });

export const searchLocations = createServerFn({ method: "GET" })
  .inputValidator(SearchParamsSchema)
  .handler(async (ctx) => {
    // Only return published locations (exclude drafts)
    const locations = await db
      .select()
      .from(location)
      .where(
        and(
          eq(location.deleted, 0),
          eq(location.hidden, 0),
          eq(location.status, "published"),
        ),
      )
      .execute();

    return locations;
  });

export const getLocation = createServerFn({ method: "GET" })
  .inputValidator(z.number())
  .handler(async (ctx) => {
    const locationId = ctx.data;

    // 1. Location laden
    let loc = await db.query.location.findFirst({
      where: eq(location.id, locationId),
    });

    if (!loc) return null;

    // For public access, only return published locations
    // Exclude drafts from public interface
    // However, allow: (1) users who own the location, (2) super admins
    if (loc.status !== "published") {
      const request = getRequest();
      if (request?.headers) {
        const userSession = await auth.api.getSession({
          headers: request.headers,
        });

        if (userSession?.user) {
          // Fetch isSuperAdmin from DB (session may not include additionalFields)
          const [dbUser] = await db
            .select({ isSuperAdmin: user.isSuperAdmin })
            .from(user)
            .where(eq(user.id, userSession.user.id))
            .limit(1);
          const isSuperAdmin = dbUser?.isSuperAdmin === true;

          if (isSuperAdmin) {
            // Super admins can see every location regardless of status
          } else {
            // Check if user owns this location
            const userLocation = await db
              .select()
              .from(locationsToUsers)
              .where(
                and(
                  eq(locationsToUsers.locationId, locationId),
                  eq(locationsToUsers.userId, userSession.user.id),
                ),
              )
              .limit(1)
              .execute();

            if (userLocation.length === 0) {
              return null;
            }
          }
        } else {
          return null;
        }
      } else {
        return null;
      }
    }

    // Helper function to safely parse JSON bilingual fields
    const parseBilingual = (
      value: unknown,
    ): { german: string; english: string } | null => {
      if (!value) return null;
      if (typeof value === "object" && value !== null) {
        // Already parsed
        return value as { german: string; english: string };
      }
      if (typeof value === "string") {
        try {
          return JSON.parse(value) as { german: string; english: string };
        } catch {
          return null;
        }
      }
      return null;
    };

    const parseStringArray = (value: unknown): string[] => {
      if (!value) return [];
      if (Array.isArray(value))
        return value.filter((v) => typeof v === "string");
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          return Array.isArray(parsed)
            ? parsed.filter((v) => typeof v === "string")
            : [];
        } catch {
          return [];
        }
      }
      return [];
    };

    const parseWebsites = (
      value: unknown,
    ): Array<{ label: string; url: string }> => {
      if (!value) return [];
      const normalize = (arr: any[]) =>
        arr
          .map((w) => ({
            label: (w?.label ?? "").toString(),
            url: (w?.url ?? "").toString(),
          }))
          .filter((w) => w.label.trim() !== "" || w.url.trim() !== "");

      if (Array.isArray(value)) return normalize(value as any[]);
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) return normalize(parsed as any[]);
        } catch {
          return [];
        }
      }
      if (typeof value === "object") {
        // Some drivers/serializers may return JSON as an object with numeric keys
        return normalize(Object.values(value as any));
      }
      return [];
    };

    const parseSocialProfiles = (
      value: unknown,
    ): Array<{ label: string; url: string }> => {
      if (!value) return [];
      const normalize = (arr: any[]) =>
        arr
          .map((p) => ({
            label: (p?.label ?? "").toString(),
            url: (p?.url ?? "").toString(),
          }))
          .filter((p) => p.label.trim() !== "" || p.url.trim() !== "");

      if (Array.isArray(value)) return normalize(value as any[]);
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed)) return normalize(parsed as any[]);
        } catch {
          return [];
        }
      }
      if (typeof value === "object") {
        return normalize(Object.values(value as any));
      }
      return [];
    };

    const parseFaq = (
      value: unknown,
    ): Array<{
      question: { german: string; english: string };
      answer: { german: string; english: string };
    }> => {
      const arr: unknown[] = (() => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") {
          try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
          } catch {
            return [];
          }
        }
        return [];
      })();

      return arr.map((item: any) => ({
        question: parseBilingual(item?.question) ?? { german: "", english: "" },
        answer: parseBilingual(item?.answer) ?? { german: "", english: "" },
      }));
    };

    let additionalAttributes = await db.query.additionalAttribute.findMany({
      where: eq(additionalAttribute.locationId, locationId),
      orderBy: (attrs, { asc }) => [
        asc(attrs.position),
        asc(attrs.id),
      ],
    });

    additionalAttributes = additionalAttributes.map((attribute) => ({
      ...attribute,
      name: parseBilingual(attribute.name),
    }));

    // Additional contract bindings (for draft editor)
    let additionalContractBindings = await db.query.additionalContractBinding.findMany(
      {
        where: eq(additionalContractBinding.locationId, locationId),
        orderBy: (t, { asc }) => [asc(t.position), asc(t.id)],
      },
    );

    additionalContractBindings = additionalContractBindings.map((binding) => ({
      ...binding,
      name: parseBilingual(binding.name) ?? { german: "", english: "" },
      active: Boolean(binding.active),
    }));

    // some adjustments - parse all bilingual JSON fields
    loc = {
      ...loc,
      nameForm: parseBilingual(loc.nameForm),
      hotelDescription: parseBilingual(loc.hotelDescription),
      descriptionForm: parseBilingual(loc.descriptionForm),
      descriptionLong: parseBilingual(loc.descriptionLong),
      contractDetails: parseBilingual(loc.contractDetails),
      locationShortDescription: parseBilingual(loc.locationShortDescription),
      additionalTechnologyInformation: parseBilingual(
        (loc as any).additionalTechnologyInformation,
      ),
      additionalSanitaryInformation: parseBilingual(
        (loc as any).additionalSanitaryInformation,
      ),
      additionalRentalInformation: parseBilingual(
        loc.additionalRentalInformation,
      ),
      additionalStageInformation: parseBilingual(
        (loc as any).additionalStageInformation,
      ),
      additionalPricingInformation: parseBilingual(
        loc.additionalPricingInformation,
      ),
      additionalCleaningCostsInformation: parseBilingual(
        loc.additionalCleaningCostsInformation,
      ),
      additionalCostsInformation: parseBilingual(
        loc.additionalCostsInformation,
      ),
      additionalLoadingDockInformation: parseBilingual(
        loc.additionalLoadingDockInformation,
      ),
      additionalParkingSpaceInformation: parseBilingual(
        loc.additionalParkingSpaceInformation,
      ),
      additionalFurnitureInformation: parseBilingual(
        loc.additionalFurnitureInformation,
      ),
      additionalLeisureInformation: parseBilingual(
        loc.additionalLeisureInformation,
      ),
      ccEmails: parseStringArray((loc as any).ccEmails),
      websites: parseWebsites((loc as any).websites),
      socialProfiles: parseSocialProfiles((loc as any).socialProfiles),
      faq: parseFaq((loc as any).faq),
    };

    // 2. Bilder (JOIN-Tabelle)
    const images = await db
      .select({
        id: file.id,
        path: file.path,
        mimetype: file.mimetype,
        alt: file.alt,
        size: file.size,
        sort: file.sort,
        categoryId: fileCategory.id,
        categoryName: fileCategory.name,
        purpose: locationsToFiles.purpose,
      })
      .from(locationsToFiles)
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(
        and(
          eq(locationsToFiles.locationId, locationId),
          notInArray(locationsToFiles.purpose, [
            "location_floorplan",
            "location_document",
          ]),
        ),
      )
      .orderBy(asc(file.sort));

    const floorplans = await db
      .select({
        id: file.id,
        path: file.path,
        mimetype: file.mimetype,
        alt: file.alt,
        size: file.size,
        sort: file.sort,
        purpose: locationsToFiles.purpose,
      })
      .from(locationsToFiles)
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .where(
        and(
          eq(locationsToFiles.locationId, locationId),
          eq(locationsToFiles.purpose, "location_floorplan"),
        ),
      )
      .orderBy(asc(file.sort));

    const documents = await db
      .select({
        id: file.id,
        path: file.path,
        mimetype: file.mimetype,
        alt: file.alt,
        size: file.size,
        sort: file.sort,
        purpose: locationsToFiles.purpose,
      })
      .from(locationsToFiles)
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .where(
        and(
          eq(locationsToFiles.locationId, locationId),
          eq(locationsToFiles.purpose, "location_document"),
        ),
      )
      .orderBy(asc(file.sort));

    // 2.5 Certificates
    const certificates = await db
      .select({
        id: locationCertificate.id,
        locationId: locationCertificate.locationId,
        validUntil: locationCertificate.validUntil,
        greenKey: locationCertificate.greenKey,
        iso14001: locationCertificate.iso14001,
        gstcHotel: locationCertificate.gstcHotel,
        active: locationCertificate.active,
        name: locationCertificate.name,
        file: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          size: file.size,
        },
      })
      .from(locationCertificate)
      .leftJoin(file, eq(file.id, locationCertificate.fileId))
      .where(eq(locationCertificate.locationId, locationId))
      .orderBy(asc(locationCertificate.id));

    // 3. Räume
    const roomRows = await db
      .select({
        locationRoom,
        roomShape: {
          id: roomShape.id,
          name: roomShape.name,
        },
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
        },
      })
      .from(locationRoom)
      .leftJoin(roomShape, eq(roomShape.id, locationRoom.roomShapeId))
      .leftJoin(
        locationroomsToFiles,
        eq(locationroomsToFiles.locationroomId, locationRoom.id),
      )
      .leftJoin(file, eq(file.id, locationroomsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(eq(locationRoom.locationId, locationId))
      .orderBy(asc(locationRoom.name));

    const roomsMap = new Map<number, RoomWithImages>();

    for (const row of roomRows) {
      const roomId = row.locationRoom.id;

      if (!roomsMap.has(roomId)) {
        roomsMap.set(roomId, {
          ...row.locationRoom,
          nameForm: parseBilingual(row.locationRoom.nameForm),
          shortDescription: parseBilingual(row.locationRoom.shortDescription),
          longDescription: parseBilingual(row.locationRoom.longDescription),
          additionalEquipmentInformation: parseBilingual(
            row.locationRoom.additionalEquipmentInformation,
          ),
          additionalPricingInformation: parseBilingual(
            row.locationRoom.additionalPricingInformation,
          ),
          additionalCleaningCostsInformation: parseBilingual(
            row.locationRoom.additionalCleaningCostsInformation,
          ),
          additionalFireProtectionInformation: parseBilingual(
            row.locationRoom.additionalFireProtectionInformation,
          ),
          roomShape:
            row.roomShape?.id != null && row.roomShape?.name != null
              ? { id: row.roomShape.id, name: row.roomShape.name }
              : null,
          images: [],
        });
      }

      if (row.image?.id) {
        roomsMap.get(roomId)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    const roomsWithImages: RoomWithImages[] = Array.from(roomsMap.values());

    // 3.4 Load additional attributes for rooms
    if (roomsWithImages.length > 0) {
      const roomIds = roomsWithImages.map((room) => room.id);
      const roomAdditionalAttributesRows = await db
        .select()
        .from(additionalAttribute)
        .where(
          and(
            inArray(additionalAttribute.roomId, roomIds),
            eq(additionalAttribute.locationId, locationId),
          ),
        );

      // Parse bilingual names and group by roomId
      const attributesByRoomId = new Map<
        number,
        Array<{
          id: number;
          name: { german: string; english: string } | null;
          purpose: string;
          active: boolean | null;
        }>
      >();
      for (const attr of roomAdditionalAttributesRows) {
        const parsedAttr = {
          id: attr.id,
          name: parseBilingual(attr.name),
          purpose: attr.purpose,
          active: attr.active,
        };
        const roomId = attr.roomId;
        if (roomId) {
          if (!attributesByRoomId.has(roomId)) {
            attributesByRoomId.set(roomId, []);
          }
          attributesByRoomId.get(roomId)!.push(parsedAttr);
        }
      }

      // Add additionalAttributes to each room
      for (const room of roomsWithImages) {
        room.additionalAttributes = attributesByRoomId.get(room.id) || [];
      }
    }

    // 3.5 hotel rooms
    const hotelRoomRows = await db
      .select({
        hotelRoom,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
        },
      })
      .from(hotelRoom)
      .leftJoin(
        hotelRoomsToFiles,
        eq(hotelRoomsToFiles.hotelRoomId, hotelRoom.id),
      )
      .leftJoin(file, eq(file.id, hotelRoomsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(eq(hotelRoom.locationId, locationId));

    const hotelRoomsMap = new Map<number, HotelRoomWithImages>();

    for (const row of hotelRoomRows) {
      const hotelRoomId = row.hotelRoom.id;

      if (!hotelRoomsMap.has(hotelRoomId)) {
        hotelRoomsMap.set(hotelRoomId, {
          ...row.hotelRoom,
          name: JSON.parse(row.hotelRoom.name as unknown as string) as {
            german: string;
            english: string;
          },
          description: JSON.parse(
            row.hotelRoom.description as unknown as string,
          ) as {
            german: string;
            english: string;
          },
          images: [],
        });
      }

      if (row.image?.id) {
        hotelRoomsMap.get(hotelRoomId)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    const hotelRoomsWithImages: HotelRoomWithImages[] = Array.from(
      hotelRoomsMap.values(),
    );

    // Main gallery: Location → each room (by name) → Hotel/Übernachtung (match old site)
    const mergedImages: Array<Image> = [];
    const CATEGORY_ORDER_LOCATION = 0;
    const CATEGORY_ORDER_HOTEL = 999;
    for (const img of images) {
      if (img.id == null || img.path == null) continue;
      mergedImages.push({
        ...img,
        id: img.id,
        path: img.path,
        categorySortOrder: CATEGORY_ORDER_LOCATION,
      });
    }
    roomsWithImages.forEach((room, roomIndex) => {
      for (const img of room.images) {
        mergedImages.push({
          ...img,
          purpose: img.purpose ?? "location_gallery_image",
          categorySortOrder: roomIndex + 1,
        });
      }
    });
    hotelRoomsWithImages.forEach((hotelRoom) => {
      for (const img of hotelRoom.images) {
        mergedImages.push({
          ...img,
          purpose: img.purpose ?? "location_gallery_image",
          categoryName: img.categoryName ?? "Übernachtung/Hotel",
          categorySortOrder: CATEGORY_ORDER_HOTEL,
        });
      }
    });

    // 4. Außenbereiche
    const outsideAreasRows = await db
      .select({
        outsidearea,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
        },
      })
      .from(outsidearea)
      .leftJoin(
        outsideareasToFiles,
        eq(outsideareasToFiles.outsideareaId, outsidearea.id),
      )
      .leftJoin(file, eq(file.id, outsideareasToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(eq(outsidearea.locationId, locationId));

    const outsideAreasMap = new Map<number, OutsideAreaWithImages>();

    for (const row of outsideAreasRows) {
      const outsideAreaId = row.outsidearea.id;

      if (!outsideAreasMap.has(outsideAreaId)) {
        outsideAreasMap.set(outsideAreaId, {
          ...row.outsidearea,
          name: parseBilingual(row.outsidearea.name),
          shortDescription: parseBilingual(row.outsidearea.shortDescription),
          longDescription: parseBilingual(row.outsidearea.longDescription),
          additionalPricingInformation: parseBilingual(
            row.outsidearea.additionalPricingInformation,
          ),
          images: [],
        });
      }

      if (row.image?.id) {
        outsideAreasMap.get(outsideAreaId)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    const outsideAreasWithImages: OutsideAreaWithImages[] = Array.from(
      outsideAreasMap.values(),
    );

    // 5. Öffnungszeiten
    const openingHours = await db.query.locationOpeningHour.findMany({
      where: eq(locationOpeningHour.locationId, locationId),
      orderBy: (t, { asc }) => asc(t.weekday),
    });

    // 6. Infrastruktur
    const infrastructure = await db.query.locationInfrastructure.findMany({
      where: eq(locationInfrastructure.locationId, locationId),
      orderBy: (t, { asc }) => asc(t.sorting),
    });

    // 7. Kontakt
    const contacts = await db.query.locationContact.findMany({
      where: eq(locationContact.locationId, locationId),
      orderBy: (t, { asc }) => asc(t.id),
    });

    const hotelTypes = await db
      .select({
        id: hotelType.id,
        name: hotelType.name,
      })
      .from(hotelType)
      .innerJoin(
        locationsToHotelTypes,
        eq(locationsToHotelTypes.hotelTypeId, hotelType.id),
      )
      .where(eq(locationsToHotelTypes.locationId, locationId));

    // 8. Event types (via join table)
    const eventTypes = await db
      .select({
        id: eventType.id,
        name: eventType.name,
      })
      .from(locationsToEventTypes)
      .leftJoin(eventType, eq(eventType.id, locationsToEventTypes.eventTypeId))
      .where(eq(locationsToEventTypes.locationId, locationId))
      .orderBy(eventType.name);

    // infra types
    const infraTypes = await db
      .select({
        id: infraType.id,
        name: infraType.name,
      })
      .from(locationsToInfraTypes)
      .leftJoin(infraType, eq(infraType.id, locationsToInfraTypes.infraTypeId))
      .where(eq(locationsToInfraTypes.locationId, locationId))
      .orderBy(infraType.name);

    // location types
    const locationTypes = await db
      .select({
        id: locationType.id,
        name: locationType.name,
      })
      .from(locationsToLocationTypes)
      .innerJoin(
        locationType,
        eq(locationType.id, locationsToLocationTypes.locationTypeId),
      )
      .where(eq(locationsToLocationTypes.locationId, locationId))
      .orderBy(locationType.name);

    return {
      ...loc,
      additionalAttributes,
      additionalContractBindings,
      images: mergedImages,
      certificates,
      floorplans,
      documents,
      rooms: roomsWithImages,
      hotelRoomInstances: hotelRoomsWithImages,
      outsideAreas: outsideAreasWithImages,
      openingHours,
      infrastructure,
      contacts,
      eventTypes,
      infraTypes,
      hotelTypes,
      locationTypes,
    };
  });

export const getLocationsByIds = createServerFn({ method: "GET" })
  .inputValidator(z.array(z.number()))
  .handler(async (ctx) => {
    const ids = ctx.data;

    if (ids.length === 0) {
      return [];
    }

    // Fetch locations with their images
    // Only return published locations (exclude drafts)
    const rows = await db
      .select({
        location,
        image: {
          id: file.id,
          path: file.path,
          mimetype: file.mimetype,
          alt: file.alt,
          size: file.size,
          sort: file.sort,
          categoryId: fileCategory.id,
          categoryName: fileCategory.name,
          purpose: locationsToFiles.purpose,
        },
      })
      .from(location)
      .leftJoin(locationsToFiles, eq(locationsToFiles.locationId, location.id))
      .leftJoin(file, eq(file.id, locationsToFiles.fileId))
      .leftJoin(filesToCategories, eq(filesToCategories.fileId, file.id))
      .leftJoin(fileCategory, eq(fileCategory.id, filesToCategories.categoryId))
      .where(
        and(
          inArray(location.id, ids),
          eq(location.deleted, 0),
          eq(location.hidden, 0),
          eq(location.status, "published"),
          notInArray(locationsToFiles.purpose, [
            "location_floorplan",
            "location_document",
          ]),
        ),
      )
      .orderBy(asc(file.sort))
      .execute();

    // Fetch event types for these locations
    const eventTypeRows = await db
      .select({
        locationId: locationsToEventTypes.locationId,
        eventType: {
          id: eventType.id,
          name: eventType.name,
        },
      })
      .from(locationsToEventTypes)
      .innerJoin(eventType, eq(eventType.id, locationsToEventTypes.eventTypeId))
      .where(inArray(locationsToEventTypes.locationId, ids))
      .orderBy(eventType.name)
      .execute();

    // Fetch location types for these locations
    const locationTypeRows = await db
      .select({
        locationId: locationsToLocationTypes.locationId,
        locationType: {
          id: locationType.id,
          name: locationType.name,
        },
      })
      .from(locationsToLocationTypes)
      .innerJoin(
        locationType,
        eq(locationType.id, locationsToLocationTypes.locationTypeId),
      )
      .where(inArray(locationsToLocationTypes.locationId, ids))
      .orderBy(locationType.name)
      .execute();

    // Fetch rooms for these locations
    const roomRows = await db
      .select({
        locationId: locationRoom.locationId,
        room: {
          id: locationRoom.id,
          name: locationRoom.name,
        },
      })
      .from(locationRoom)
      .where(inArray(locationRoom.locationId, ids))
      .orderBy(locationRoom.name)
      .execute();

    const locationsMap = new Map<number, LocationWithImages>();
    for (const row of rows) {
      if (!locationsMap.has(row.location.id)) {
        locationsMap.set(row.location.id, {
          ...row.location,
          // Parse bilingual JSON fields that might come as strings from the database
          descriptionForm: parseAndNormalizeBilingual(
            row.location.descriptionForm,
          ),
          images: [],
          eventTypes: [],
          locationTypes: [],
          rooms: [],
        });
      }
      if (row.image?.id) {
        locationsMap.get(row.location.id)!.images.push({
          ...row.image,
          id: row.image.id!,
          path: row.image.path!,
        });
      }
    }

    // Add event types
    for (const row of eventTypeRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.eventType?.id) {
        loc.eventTypes.push(row.eventType);
      }
    }

    // Add location types
    for (const row of locationTypeRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.locationType?.id) {
        loc.locationTypes.push(row.locationType);
      }
    }

    // Add rooms
    for (const row of roomRows) {
      const loc = locationsMap.get(row.locationId);
      if (loc && row.room?.id) {
        loc.rooms!.push(row.room);
      }
    }

    for (const loc of locationsMap.values()) {
      loc.images.sort((a, b) => {
        const sortA = a.sort ?? Number.MAX_SAFE_INTEGER;
        const sortB = b.sort ?? Number.MAX_SAFE_INTEGER;
        return sortA - sortB;
      });
      loc.images = limitImagesByPlan(loc.images, loc.plan);
    }

    // Sort locations to match the original order from ids array
    const sortedLocations = ids
      .map((id) => locationsMap.get(id))
      .filter((loc): loc is LocationWithImages => loc !== undefined);

    return sortedLocations;
  });

export const getNumberOfMatchingLocations = createServerFn({ method: "GET" })
  .inputValidator(SearchParamsSchema)
  .handler(async (ctx) => {
    const params = ctx.data;

    const where = await getLocationSearchWhereClause(params);
    const query = db
      .select({ count: count() })
      .from(location)
      .where(and(...where));
    const [data] = await query.execute();
    return data.count;
  });

/**
 * Returns the budget filter condition (price in range) or null when no budget range is set.
 * Used for matching (condition) and deviating (NOT(condition)) when budget split is active.
 */
function getBudgetCondition(params: SearchParams): any {
  if (!params.filters?.budget) return null;
  const budget = params.filters.budget;
  const budgetType = budget.type || "rateDaily";
  const currency = budget.currency || "EUR";
  const budgetFrom = budget.from;
  const budgetTo = budget.to;

  const hasBudgetRange =
    (budgetFrom !== null && budgetFrom > 0) ||
    (budgetTo !== null && budgetTo > 0);
  if (!hasBudgetRange) return null;

  const basicConditions: any[] = [];
  if (currency) {
    basicConditions.push(
      or(
        eq(location.currency, currency),
        sql`${location.currency} IS NULL`,
      ),
    );
  }

  let priceField: any;
  switch (budgetType) {
    case "rateDaily":
      priceField = location.rateDaily;
      break;
    case "rateHourly":
      priceField = location.rateHourly;
      break;
    case "minBudgetTotal":
      priceField = location.minimumConsumptionPrice;
      break;
    case "minBudgetPerPerson":
      // "Pauschale pro Person" corresponds to the consumption flat rate per person
      priceField = location.consumptionFlatRatePerPerson;
      break;
    default:
      priceField = location.rateDaily;
  }

  let priceCondition: any;
  if (
    budgetFrom !== null &&
    budgetFrom > 0 &&
    budgetTo !== null &&
    budgetTo > 0
  ) {
    priceCondition = and(
      gte(priceField, budgetFrom),
      lte(priceField, budgetTo),
    );
  } else if (budgetFrom !== null && budgetFrom > 0) {
    priceCondition = gte(priceField, budgetFrom);
  } else if (budgetTo !== null && budgetTo > 0) {
    priceCondition = lte(priceField, budgetTo);
  } else {
    return null;
  }

  if (basicConditions.length > 0) {
    return and(...basicConditions, priceCondition);
  }
  return priceCondition;
}

/**
 * Returns the "deviating" budget condition: locations that do NOT match the budget filter.
 * Built explicitly (no NOT()) so SQL generation is reliable across drivers.
 */
function getDeviatingBudgetCondition(params: SearchParams): any {
  if (!params.filters?.budget) return null;
  const budget = params.filters.budget;
  const budgetType = budget.type || "rateDaily";
  const currency = budget.currency || "EUR";
  const budgetFrom = budget.from;
  const budgetTo = budget.to;

  const hasBudgetRange =
    (budgetFrom !== null && budgetFrom > 0) ||
    (budgetTo !== null && budgetTo > 0);
  if (!hasBudgetRange) return null;

  let priceField: any;
  switch (budgetType) {
    case "rateDaily":
      priceField = location.rateDaily;
      break;
    case "rateHourly":
      priceField = location.rateHourly;
      break;
    case "minBudgetTotal":
      priceField = location.minimumConsumptionPrice;
      break;
    case "minBudgetPerPerson":
      priceField = location.consumptionFlatRatePerPerson;
      break;
    default:
      priceField = location.rateDaily;
  }

  // Deviating = currency doesn't match OR price outside range (or null)
  const deviatingParts: any[] = [];

  if (currency) {
    deviatingParts.push(
      and(
        sql`${location.currency} IS NOT NULL`,
        sql`${location.currency} <> ${currency}`,
      ),
    );
  }
  if (
    budgetFrom !== null &&
    budgetFrom > 0 &&
    budgetTo !== null &&
    budgetTo > 0
  ) {
    deviatingParts.push(or(lt(priceField, budgetFrom), gt(priceField, budgetTo)));
    deviatingParts.push(sql`${priceField} IS NULL`);
  } else if (budgetFrom !== null && budgetFrom > 0) {
    deviatingParts.push(lt(priceField, budgetFrom));
    deviatingParts.push(sql`${priceField} IS NULL`);
  } else if (budgetTo !== null && budgetTo > 0) {
    deviatingParts.push(gt(priceField, budgetTo));
    deviatingParts.push(sql`${priceField} IS NULL`);
  }

  if (deviatingParts.length === 0) return null;
  return or(...deviatingParts);
}

function buildExpandedBoundsCondition(args: {
  locationString: string;
  mode: string;
  source: string | null;
  boundaries: {
    northeast: { lat: number; lng: number };
    southwest: { lat: number; lng: number };
  };
  centerLat: number;
  radiusKm: number;
  hasCity?: boolean;
  hasState?: boolean;
  hasCountry?: boolean;
}) {
  const {
    locationString,
    mode,
    source,
    boundaries,
    centerLat,
    radiusKm,
    hasCity,
    hasState,
    hasCountry,
  } = args;
  const latExpansion = radiusKm / 111.0;
  const lngExpansion =
    radiusKm / (111.0 * Math.cos((centerLat * Math.PI) / 180));

  console.log("[location search]", {
    locationString,
    mode,
    source,
    radiusKm,
    hasCity,
    hasState,
    hasCountry,
    original: {
      southwest: boundaries.southwest,
      northeast: boundaries.northeast,
    },
    expanded: {
      southwest: {
        lat: boundaries.southwest.lat - latExpansion,
        lng: boundaries.southwest.lng - lngExpansion,
      },
      northeast: {
        lat: boundaries.northeast.lat + latExpansion,
        lng: boundaries.northeast.lng + lngExpansion,
      },
    },
  });

  return and(
    between(
      location.lat,
      boundaries.southwest.lat - latExpansion,
      boundaries.northeast.lat + latExpansion,
    ),
    between(
      location.lng,
      boundaries.southwest.lng - lngExpansion,
      boundaries.northeast.lng + lngExpansion,
    ),
  );
}

/** Haversine radius filter; clamp ACOS arg to [-1, 1] for float stability. */
function buildRadiusCondition(args: {
  centerLat: number;
  centerLng: number;
  radiusKm: number;
}) {
  const { centerLat, centerLng, radiusKm } = args;
  return sql`(6371 * acos(
    least(1, greatest(-1,
      cos(radians(${centerLat})) *
      cos(radians(${location.lat})) *
      cos(radians(${location.lng}) - radians(${centerLng})) +
      sin(radians(${centerLat})) *
      sin(radians(${location.lat}))
    ))
  )) <= ${radiusKm}`;
}

const getLocationSearchWhereClause = async (
  params: SearchParams,
  includeBudget: boolean = true,
  /**
   * Callsite for Google cost attribution. The list and the map-marker query run
   * the same location resolution, so passing this through is what makes their
   * Place Details / Geocoding calls distinguishable in `lg_maps_api_usage`.
   */
  source: MapsCallSource = "search_list",
  resolvedLocations?: Array<ResolvedSearchLocation | null>,
): Promise<any[]> => {
  const where: any[] = [
    // Always exclude deleted and hidden locations
    eq(location.deleted, 0),
    eq(location.hidden, 0),
    // Only show published locations (exclude drafts)
    eq(location.status, "published"),
  ];

  // Filter by location ID (exact) or location name (partial match)
  const locationIdOrName = params.locationIdOrName?.trim();
  if (locationIdOrName && locationIdOrName.length > 0) {
    const parsedId = parseInt(locationIdOrName, 10);
    const isNumericId =
      String(parsedId) === locationIdOrName && parsedId > 0;
    if (isNumericId) {
      where.push(eq(location.id, parsedId));
    } else {
      // Escape LIKE special characters: \ % _
      const escaped = locationIdOrName
        .replace(/\\/g, "\\\\")
        .replace(/%/g, "\\%")
        .replace(/_/g, "\\_");
      where.push(like(location.name, `%${escaped}%`));
    }
  }

  // Location search using geocoding + expanded bounds, or by Bundesland/Kanton (state)
  // Support multiple locations: locations must be within at least one of the specified areas (OR logic)
  // If the user enters a state name (e.g. "Bayern", "Zürich"), filter by location.state instead of geocoding.
  // When map bounds are also present they are AND-ed separately (in getLocationsWithGeoBounds),
  // so this condition is always evaluated regardless of map mode.
  if (params.location && params.location.length > 0) {
    const stateCodes = new Set<string>();
    const locationConditions: any[] = [];

    for (let i = 0; i < params.location.length; i++) {
      const locationString = params.location[i];
      if (!locationString || locationString.trim() === "") {
        continue;
      }

      const stateCode = getStateCodeFromLabel(locationString);
      // For state names (Bayern, Zürich, etc.), use state-based filtering only.
      // searchRadius is ignored—geocoding + bounds expansion would incorrectly
      // expand the state's bounding box (e.g. Bavaria's box + 100km includes Prague).
      if (stateCode) {
        stateCodes.add(stateCode);
        continue;
      }

      const placeId =
        params.placeIds && params.placeIds.length > i
          ? (params.placeIds[i]?.trim() ?? "")
          : "";

      if (placeId) {
        const details = await getPlaceDetails({
          data: { placeId, source },
        });
        if (details) {
          if (resolvedLocations) {
            resolvedLocations[i] = {
              lat: details.lat,
              lng: details.lng,
              viewport: details.viewport,
              bounds: details.bounds,
            };
          }
          // Country-level places (e.g. "Spanien"): match stored country name.
          // A fixed radius around the country centroid would miss almost all venues.
          const isCountryLevel =
            details.hasCountry &&
            !details.hasCity &&
            !details.hasState &&
            !!details.country;

          if (isCountryLevel) {
            const countryCondition = eq(location.country, details.country);
            const placeBoundaries = details.bounds || details.viewport;
            if (placeBoundaries) {
              console.log("[location search]", {
                locationString,
                mode: "place-details-country-or-bounds",
                country: details.country,
              });
              locationConditions.push(
                or(
                  countryCondition,
                  buildExpandedBoundsCondition({
                    locationString,
                    mode: "place-details-country-expanded-bounds",
                    source: details.bounds ? "bounds" : "viewport",
                    boundaries: placeBoundaries,
                    centerLat: details.lat,
                    radiusKm: params.searchRadius ?? 20,
                    hasCity: details.hasCity,
                    hasState: details.hasState,
                    hasCountry: details.hasCountry,
                  }),
                ),
              );
            } else {
              console.log("[location search]", {
                locationString,
                mode: "place-details-country",
                country: details.country,
              });
              locationConditions.push(countryCondition);
            }
            continue;
          }

          const placeBoundaries = details.bounds || details.viewport;
          if (placeBoundaries) {
            locationConditions.push(
              buildExpandedBoundsCondition({
                locationString,
                mode: "place-details-expanded-bounds",
                source: details.bounds ? "bounds" : "viewport",
                boundaries: placeBoundaries,
                centerLat: details.lat,
                radiusKm: params.searchRadius ?? 20,
                hasCity: details.hasCity,
                hasState: details.hasState,
                hasCountry: details.hasCountry,
              }),
            );
            continue;
          }

          // Point-only Place Details: use radius from center — do not geocode.
          const radiusKm = params.searchRadius ?? 20;
          const centerLat = details.lat;
          const centerLng = details.lng;
          console.log("[location search]", {
            locationString,
            mode: "place-details-radius",
            reason: "no bounds/viewport on place details",
            center: { lat: centerLat, lng: centerLng },
            radiusKm,
          });
          locationConditions.push(
            buildRadiusCondition({ centerLat, centerLng, radiusKm }),
          );
          continue;
        }

        console.log("[location search]", {
          locationString,
          mode: "place-details-fallback-geocode",
          reason: "place_details returned no result",
        });
        // Fall through to geocode only when Place Details are missing entirely.
      }

      const geocodeResult = await geocodeLocationInternal(locationString, {
        source,
      });

      if (geocodeResult) {
        if (resolvedLocations) {
          resolvedLocations[i] = {
            lat: geocodeResult.lat,
            lng: geocodeResult.lng,
            viewport: geocodeResult.viewport,
            bounds: geocodeResult.bounds,
          };
        }
        // Use geocoded bounds (bounding box) when available so locations inside the
        // place's geographic extent are included (e.g. all of Munich, not just within X km of center).
        // Fall back to radius from center only when the geocoder returns no bounds/viewport.
        const boundaries = geocodeResult.bounds || geocodeResult.viewport;
        const boundariesSource = geocodeResult.bounds
          ? "bounds"
          : geocodeResult.viewport
            ? "viewport"
            : null;

        if (boundaries) {
          locationConditions.push(
            buildExpandedBoundsCondition({
              locationString,
              mode: "expanded-bounds",
              source: boundariesSource,
              boundaries,
              centerLat: geocodeResult.lat,
              radiusKm: params.searchRadius ?? 20,
              hasCity: geocodeResult.hasCity,
              hasState: geocodeResult.hasState,
              hasCountry: geocodeResult.hasCountry,
            }),
          );
        } else {
          // Fallback: no bounds/viewport (rare); use radius from center.
          const radiusKm = params.searchRadius ?? 20;
          const centerLat = geocodeResult.lat;
          const centerLng = geocodeResult.lng;
          console.log("[location search]", {
            locationString,
            mode: "radius",
            reason: "no bounds/viewport",
            center: { lat: centerLat, lng: centerLng },
            radiusKm,
            searchRadiusFromParams: params.searchRadius,
          });
          locationConditions.push(
            buildRadiusCondition({ centerLat, centerLng, radiusKm }),
          );
        }
      } else {
        console.log("[location search]", {
          locationString,
          mode: "skipped",
          reason: "geocode returned no result",
        });
      }
      // If geocoding fails for one location, we skip it (graceful degradation)
    }

    const stateConditions =
      stateCodes.size > 0
        ? [inArray(location.state, Array.from(stateCodes))]
        : [];
    const allLocationConditions = [...stateConditions, ...locationConditions];
    if (allLocationConditions.length > 0) {
      where.push(or(...allLocationConditions));
    }
  }

  if (params.guests !== "" && params.guests !== "any") {
    const [operator, value] = params.guests.split("-");
    const guestCount = parseInt(value);
    if (operator === "gte") {
      // "mind. X" (at least X) → location capacity must be >= guestCount
      // Priority: maxCapacity > capacityStanding > seatingStanding
      // maxCapacity and capacityStanding are only considered if they are > 0
      const locationCapacity = sql`COALESCE(
        NULLIF(${location.maxCapacity}, 0),
        NULLIF(${location.capacityStanding}, 0),
        COALESCE(${location.seatingStanding}, 0)
      )`;

      where.push(sql`${locationCapacity} >= ${guestCount}`);
    }
  }

  // Handle top-level type parameter (event type/occasion)
  if (params.type !== null && params.type !== undefined) {
    const subquery = db
      .select({ id: locationsToEventTypes.locationId })
      .from(locationsToEventTypes)
      .innerJoin(eventType, eq(locationsToEventTypes.eventTypeId, eventType.id))
      .where(eq(eventType.id, params.type));

    where.push(inArray(location.id, subquery));
  }

  if (params.filters?.occasion && params.filters.occasion.length > 0) {
    const subquery = db
      .select({ id: locationsToEventTypes.locationId })
      .from(locationsToEventTypes)
      .innerJoin(eventType, eq(locationsToEventTypes.eventTypeId, eventType.id))
      .where(inArray(eventType.id, params.filters.occasion));

    where.push(inArray(location.id, subquery));
  }

  if (params.filters?.locationType && params.filters.locationType.length > 0) {
    const subquery = db
      .select({ id: locationsToLocationTypes.locationId })
      .from(locationsToLocationTypes)
      .innerJoin(
        locationType,
        eq(locationsToLocationTypes.locationTypeId, locationType.id),
      )
      .where(inArray(locationType.id, params.filters.locationType));

    where.push(inArray(location.id, subquery));
  }

  if (params.filters?.numberOfParkingSpaces) {
    // Filter by parking space count
    // Include locations with "yes" (own parking) or "noOwn" (no own parking, but parking may be available nearby)
    // Exclude only locations marked as "no" (no parking available at all)
    where.push(
      and(
        or(eq(location.parkingLots, "yes"), eq(location.parkingLots, "noOwn")),
        gte(location.parkingSpaceCount, params.filters.numberOfParkingSpaces),
      ),
    );
  }

  // Filter based on parking lots availability
  // If undefined: show all locations (no filter)
  // If "yes": show locations with own parking lots
  // If "noOwn": show locations with nearby parking lots (no own parking)
  if (params.filters?.parkingLots) {
    // Filter for locations with specific parking lot type
    where.push(eq(location.parkingLots, params.filters.parkingLots));
  }

  // Filter for accommodations
  if (params.filters?.hasAccommodations === true) {
    where.push(eq(location.accommodations, true));
  }

  // Filter by hotel rooms count
  if (params.filters?.hotelRooms && params.filters.hotelRooms > 0) {
    where.push(gte(location.hotelRooms, params.filters.hotelRooms));
  }

  // Filter by hotel stars
  if (params.filters?.hotelStars && params.filters.hotelStars > 0) {
    where.push(eq(location.hotelStars, params.filters.hotelStars));
  }

  // Filter by hotel types
  if (params.filters?.hotelTypes && params.filters.hotelTypes.length > 0) {
    const subquery = db
      .select({ locationId: locationsToHotelTypes.locationId })
      .from(locationsToHotelTypes)
      .where(inArray(locationsToHotelTypes.hotelTypeId, params.filters.hotelTypes));

    where.push(inArray(location.id, subquery));
  }

  if (params.filters?.stage) {
    where.push(eq(location.stage, params.filters.stage));
  }

  if (params.filters?.locationSettings?.indoor) {
    // Filter for indoor-only locations
    where.push(eq(location.areaType, "indoor"));
  }

  if (params.filters?.locationSettings?.outdoor) {
    // Filter for outdoor locations
    where.push(eq(location.areaType, "outdoor"));
  }

  // Location settings - mixed (indoor with outdoor area)
  if (params.filters?.locationSettings?.mixed) {
    // Filter for mixed locations (indoor with outdoor area)
    where.push(eq(location.areaType, "mixed"));
  }

  // Location size filters
  if (
    params.filters?.locationSize?.maxArea &&
    params.filters.locationSize.maxArea > 0
  ) {
    where.push(gte(location.maxArea, params.filters.locationSize.maxArea));
  }

  if (
    params.filters?.locationSize?.indoorAreaTotal &&
    params.filters.locationSize.indoorAreaTotal > 0
  ) {
    where.push(lte(location.indoorAreaTotal, params.filters.locationSize.indoorAreaTotal));
  }

  if (
    params.filters?.locationSize?.maxHeight &&
    params.filters.locationSize.maxHeight > 0
  ) {
    where.push(lte(location.maxHeight, params.filters.locationSize.maxHeight));
  }

  // Location dates
  if (params.filters?.locationOpening && params.filters.locationOpening > 0) {
    where.push(gte(location.openingYear, params.filters.locationOpening));
  }

  if (
    params.filters?.locationRenovation &&
    params.filters.locationRenovation > 0
  ) {
    where.push(
      gte(location.yearOfRenovation, params.filters.locationRenovation),
    );
  }

  // Common properties - check if ANY room has these properties
  if (params.filters?.commonProperties?.accessible) {
    // Use EXISTS subquery to explicitly check if location has at least one accessible room
    where.push(
      sql`EXISTS (
        SELECT 1 
        FROM ${locationRoom} 
        WHERE ${locationRoom.locationId} = ${location.id} 
        AND ${locationRoom.accessible} = 1
      )`,
    );
  }

  if (params.filters?.commonProperties?.dimmable) {
    where.push(
      sql`EXISTS (
        SELECT 1 
        FROM ${locationRoom} 
        WHERE ${locationRoom.locationId} = ${location.id} 
        AND ${locationRoom.dimmableLighting} = 1
      )`,
    );
  }

  if (params.filters?.commonProperties?.daylight) {
    where.push(
      sql`EXISTS (
        SELECT 1 
        FROM ${locationRoom} 
        WHERE ${locationRoom.locationId} = ${location.id} 
        AND ${locationRoom.daylight} = 1
      )`,
    );
  }

  // Room requirements - count rooms dynamically via subquery
  // Note: totalRooms field exists but is never updated, so we count actual rooms
  if (
    params.filters?.numberOfNeededRooms &&
    params.filters.numberOfNeededRooms > 0
  ) {
    const roomCountSubquery = db
      .select({ id: locationRoom.locationId })
      .from(locationRoom)
      .groupBy(locationRoom.locationId)
      .having(sql`count(*) >= ${params.filters.numberOfNeededRooms}`);

    where.push(inArray(location.id, roomCountSubquery));
  }

  if (params.filters?.outerAreaSize && params.filters.outerAreaSize > 0) {
    where.push(
      or(
        gte(location.outsideAreaSum, params.filters.outerAreaSize),
        gte(location.outsideLargestArea, params.filters.outerAreaSize),
      ),
    );
  }

  // Seating filters
  if (params.filters?.seating && params.filters.seating !== "") {
    const seatingConditions: any[] = [];
    switch (params.filters.seating) {
      case "party":
        seatingConditions.push(
          gt(location.seatingParty, 0),
        );
        break;
      case "standing-reception":
        seatingConditions.push(
          gt(location.seatingStanding, 0),
        );
        break;
      case "block":
        seatingConditions.push(
          gt(location.seatingBlock, 0),
        );
        break;
      case "banquet":
        seatingConditions.push(
          gt(location.seatingBanquet, 0),
        );
        break;
      case "row":
        seatingConditions.push(
          gt(location.seatingRow, 0),
        );
        break;
      case "parliament":
        seatingConditions.push(
          gt(location.seatingParliamentary, 0),
        );
        break;
      case "u-shaped":
        seatingConditions.push(
          gt(location.seatingUShape, 0),
        );
        break;
      case "table-seating":
        seatingConditions.push(
          gt(location.seatingTable, 0),
        );
        break;
    }
    if (seatingConditions.length > 0) {
      where.push(or(...seatingConditions));
    }
  }

  // Budget filters - filter by selected price field type (optional; when includeBudget false, used for budget-split deviating segment)
  if (includeBudget) {
    const budgetCondition = getBudgetCondition(params);
    if (budgetCondition) {
      where.push(budgetCondition);
    } else if (params.filters?.budget?.type) {
      // When a budget type is selected but no numeric range is set,
      // filter to locations that actually have a value for the chosen price field.
      const budgetType = params.filters.budget.type || "rateDaily";

      let priceField: any;
      switch (budgetType) {
        case "rateDaily":
          priceField = location.rateDaily;
          break;
        case "rateHourly":
          priceField = location.rateHourly;
          break;
        case "minBudgetTotal":
          priceField = location.minimumConsumptionPrice;
          break;
        case "minBudgetPerPerson":
          priceField = location.consumptionFlatRatePerPerson;
          break;
        default:
          priceField = location.rateDaily;
      }

      where.push(gt(priceField, 0));
    }
  }

  // Contract bindings - catering
  const cateringBinding = params.filters?.cateringBinding;
  if (cateringBinding === "none") {
    where.push(eq(location.catering, "free"));
  } else if (cateringBinding === "fixed") {
    where.push(
      or(
        eq(location.catering, "permanentPartner"),
        eq(location.catering, "freeChoicePaid"),
        eq(location.catering, "multiplePartner"),
      )
    );
  }

  // Contract bindings - tech
  const techBinding = params.filters?.techBinding;
  if (techBinding === "none") {
    where.push(eq(location.tech, "free"));
  } else if (techBinding === "fixed") {
    where.push(
      or(
        eq(location.tech, "permanentPartner"),
        eq(location.tech, "freeChoicePaid"),
        eq(location.tech, "multiplePartner"),
      ),
    );
  }

  // Infrastructure filter (already implemented but keeping for completeness)
  if (
    params.filters?.infrastructure &&
    params.filters.infrastructure.length > 0
  ) {
    const infraSubquery = db
      .select({ id: locationsToInfraTypes.locationId })
      .from(locationsToInfraTypes)
      .innerJoin(infraType, eq(locationsToInfraTypes.infraTypeId, infraType.id))
      .where(inArray(infraType.id, params.filters.infrastructure));

    where.push(inArray(location.id, infraSubquery));
  }

  // Distances filter - check locationInfrastructure table
  if (params.filters?.distances && params.filters.distances.length > 0) {
    const distanceConditions = params.filters.distances.map((distance) => {
      const subquery = db
        .select({ id: locationInfrastructure.locationId })
        .from(locationInfrastructure)
        .where(
          and(
            eq(locationInfrastructure.type, distance.type),
            lte(locationInfrastructure.distance, distance.maxDistance),
          ),
        );
      return inArray(location.id, subquery);
    });

    if (distanceConditions.length > 0) {
      where.push(and(...distanceConditions));
    }
  }

  // Rental duration - check locationOpeningHour table
  if (
    params.filters?.rentalDuration &&
    (params.filters.rentalDuration.days.length > 0 ||
      params.filters.rentalDuration.from !== null ||
      params.filters.rentalDuration.to !== null)
  ) {
    // Only include locations where "rent out of hours" is NOT possible.
    // Treat NULL as false (legacy / unset).
    where.push(
      or(
        eq(location.rentalOutOfHoursPossible, false),
        sql`${location.rentalOutOfHoursPossible} IS NULL`,
      ),
    );

    const weekdayMap: Record<string, number> = {
      monday: 0,
      tuesday: 1,
      wednesday: 2,
      thursday: 3,
      friday: 4,
      saturday: 5,
      sunday: 6,
    };

    const openingHourConditions: any[] = [];

    // Filter by days
    if (params.filters.rentalDuration.days.length > 0) {
      const weekdays = params.filters.rentalDuration.days.map(
        (day) => weekdayMap[day],
      );
      openingHourConditions.push(
        inArray(locationOpeningHour.weekday, weekdays),
      );
    }

    // Filter by time range
    if (
      params.filters.rentalDuration.from !== null ||
      params.filters.rentalDuration.to !== null
    ) {
      const rawFrom =
        params.filters.rentalDuration.from !== null
          ? `${params.filters.rentalDuration.from
            .toString()
            .padStart(2, "0")}:00`
          : null;
      const rawTo =
        params.filters.rentalDuration.to !== null
          ? `${params.filters.rentalDuration.to
            .toString()
            .padStart(2, "0")}:00`
          : null;

      // Special case: 00:00–00:00 should mean "open the whole day".
      // We approximate this as 00:00–23:59 and then apply the usual
      // "open for the entire requested interval" logic below.
      const fromTime =
        rawFrom === "00:00" && rawTo === "00:00" ? "00:00" : rawFrom;
      const toTime =
        rawFrom === "00:00" && rawTo === "00:00" ? "23:59" : rawTo;

      // Check if location is open during the requested time.
      // A location qualifies if:
      // - its opening interval fully contains the requested interval, OR
      // - it's 24h: from_time = until_time (e.g. 00:00–00:00 or 07:00–07:00).
      const is24h =
        eq(locationOpeningHour.fromTime, locationOpeningHour.untilTime);
      const timeConditions: any[] = [];

      if (fromTime !== null) {
        timeConditions.push(
          or(
            // Normal case: opening starts at or before requested start
            lte(locationOpeningHour.fromTime, fromTime),
            // 24h case (same from/until = open all day)
            is24h,
          ),
        );
      }

      if (toTime !== null) {
        timeConditions.push(
          or(
            // Normal case: opening ends at or after requested end
            gte(locationOpeningHour.untilTime, toTime),
            // 24h case (same from/until = open all day)
            is24h,
          ),
        );
      }

      if (timeConditions.length > 0) {
        openingHourConditions.push(and(...timeConditions));
      }
    }

    if (openingHourConditions.length > 0) {
      const rentalSubquery = db
        .select({ id: locationOpeningHour.locationId })
        .from(locationOpeningHour)
        .where(and(...openingHourConditions));
      where.push(inArray(location.id, rentalSubquery));
    }
  }

  // Delivery logistics
  if (params.filters?.deliveryLogistics?.truckAccess) {
    where.push(eq(location.accessibleForTrucks, true));
  }

  if (params.filters?.deliveryLogistics?.carAccess) {
    where.push(eq(location.accessibleForCars, true));
  }

  if (params.filters?.deliveryLogistics?.freightElevator) {
    where.push(eq(location.freightElevator, true));
  }

  if (params.filters?.deliveryLogistics?.loadingRamp) {
    where.push(eq(location.loadingDock, true));
  }

  // Leisure / Sport / Wellness
  if (params.filters?.leisureSportWellness?.leisure?.sightseeing) {
    where.push(eq(location.sightseeing, true));
  }

  if (params.filters?.leisureSportWellness?.sport?.golf) {
    where.push(eq(location.golf, true));
  }

  if (params.filters?.leisureSportWellness?.sport?.tennis) {
    where.push(eq(location.tennis, true));
  }

  if (params.filters?.leisureSportWellness?.sport?.sailing) {
    where.push(eq(location.sailing, true));
  }

  if (params.filters?.leisureSportWellness?.sport?.ski) {
    where.push(eq(location.ski, true));
  }

  if (params.filters?.leisureSportWellness?.wellness?.sauna) {
    // spa field likely includes sauna
    where.push(eq(location.spa, true));
  }

  if (params.filters?.leisureSportWellness?.wellness?.massage) {
    where.push(eq(location.massage, true));
  }

  if (params.filters?.leisureSportWellness?.wellness?.indoorPool) {
    where.push(eq(location.swimmingIndoor, true));
  }

  if (params.filters?.leisureSportWellness?.wellness?.outdoorPool) {
    where.push(eq(location.swimmingOutdoor, true));
  }

  // Certifications
  if (params.filters?.certifications?.gstcHotel) {
    const gstcSubquery = db
      .select({ id: locationCertificate.locationId })
      .from(locationCertificate)
      .where(eq(locationCertificate.gstcHotel, true));
    where.push(inArray(location.id, gstcSubquery));
  }

  if (params.filters?.certifications?.greenKey) {
    const greenKeySubquery = db
      .select({ id: locationCertificate.locationId })
      .from(locationCertificate)
      .where(eq(locationCertificate.greenKey, true));
    where.push(inArray(location.id, greenKeySubquery));
  }

  if (params.filters?.certifications?.iso14001) {
    const isoSubquery = db
      .select({ id: locationCertificate.locationId })
      .from(locationCertificate)
      .where(eq(locationCertificate.iso14001, true));
    where.push(inArray(location.id, isoSubquery));
  }

  // Equipment - Power
  if (params.filters?.equipment?.acPower?.volt110) {
    where.push(eq(location.v110, true));
  }

  if (params.filters?.equipment?.acPower?.volt230) {
    where.push(eq(location.v230, true));
  }

  if (params.filters?.equipment?.threePhasePower?.amp16) {
    where.push(eq(location.a16, true));
  }

  if (params.filters?.equipment?.threePhasePower?.amp32) {
    where.push(eq(location.a32, true));
  }

  if (params.filters?.equipment?.threePhasePower?.amp64) {
    where.push(eq(location.a63, true));
  }

  if (params.filters?.equipment?.threePhasePower?.amp128) {
    where.push(eq(location.a128, true));
  }

  // Equipment - Technology
  if (params.filters?.equipment?.technology?.lighting) {
    where.push(eq(location.lighting, true));
  }

  if (params.filters?.equipment?.technology?.presentationVideo) {
    where.push(eq(location.presentationAndVideo, true));
  }

  if (params.filters?.equipment?.technology?.audio) {
    where.push(eq(location.sound, true));
  }

  if (params.filters?.equipment?.technology?.heatingClimate) {
    where.push(eq(location.heatingAndAirConditioning, true));
  }

  // Equipment - Special Technology (rigging + v1-v5)
  if (params.filters?.equipment?.specialTechnology?.rigging) {
    where.push(eq(location.rigging, true));
  }

  // For v1-v5, check locationTechnology table
  if (params.filters?.equipment?.specialTechnology?.v1) {
    const v1Subquery = db
      .select({ id: locationTechnology.locationId })
      .from(locationTechnology)
      .where(eq(locationTechnology.name, "v1"));
    where.push(inArray(location.id, v1Subquery));
  }

  if (params.filters?.equipment?.specialTechnology?.v2) {
    const v2Subquery = db
      .select({ id: locationTechnology.locationId })
      .from(locationTechnology)
      .where(eq(locationTechnology.name, "v2"));
    where.push(inArray(location.id, v2Subquery));
  }

  if (params.filters?.equipment?.specialTechnology?.v3) {
    const v3Subquery = db
      .select({ id: locationTechnology.locationId })
      .from(locationTechnology)
      .where(eq(locationTechnology.name, "v3"));
    where.push(inArray(location.id, v3Subquery));
  }

  if (params.filters?.equipment?.specialTechnology?.v4) {
    const v4Subquery = db
      .select({ id: locationTechnology.locationId })
      .from(locationTechnology)
      .where(eq(locationTechnology.name, "v4"));
    where.push(inArray(location.id, v4Subquery));
  }

  if (params.filters?.equipment?.specialTechnology?.v5) {
    const v5Subquery = db
      .select({ id: locationTechnology.locationId })
      .from(locationTechnology)
      .where(eq(locationTechnology.name, "v5"));
    where.push(inArray(location.id, v5Subquery));
  }

  // Equipment - Internet
  if (params.filters?.equipment?.internet?.g5) {
    where.push(eq(location.connection5G, true));
  }

  if (params.filters?.equipment?.internet?.g4) {
    where.push(eq(location.connection4G, true));
  }

  if (params.filters?.equipment?.internet?.g3) {
    where.push(eq(location.connection3G, true));
  }

  if (params.filters?.equipment?.internet?.g2) {
    where.push(eq(location.connection2G, true));
  }

  if (
    params.filters?.equipment?.internet?.minBandwidth &&
    params.filters.equipment.internet.minBandwidth > 0
  ) {
    where.push(
      gte(location.bandWidth, params.filters.equipment.internet.minBandwidth),
    );
  }

  // Equipment - Furniture
  if (
    params.filters?.equipment?.furniture &&
    params.filters.equipment.furniture !== "none"
  ) {
    if (params.filters.equipment.furniture === "available") {
      where.push(eq(location.furnished, true));
    } else if (params.filters.equipment.furniture === "bookable") {
      where.push(eq(location.furnishedForm, "bookable"));
    }
  }

  // Equipment - Sanitary
  if (params.filters?.equipment?.sanitary?.wcWomen) {
    where.push(
      or(eq(location.wcFemale, true), gt(location.femaleToiletCount, 0)),
    );
  }

  if (params.filters?.equipment?.sanitary?.wcMen) {
    where.push(or(eq(location.wcMale, true), gt(location.maleToiletCount, 0)));
  }

  if (params.filters?.equipment?.sanitary?.wcAccessible) {
    where.push(
      or(
        eq(location.wcAccessible, true),
        gt(location.accessibleToiletCount, 0),
      ),
    );
  }

  // Equipment - Other
  if (params.filters?.equipment?.other?.smokingArea) {
    where.push(eq(location.smokingArea, true));
  }

  if (params.filters?.equipment?.other?.artistDressingRooms) {
    where.push(eq(location.artistDressingRooms, true));
  }

  if (params.filters?.equipment?.other?.noNoiseRestrictions) {
    where.push(eq(location.noNoiseControlRequirements, true));
  }

  if (params.filters?.equipment?.other?.shower) {
    where.push(eq(location.showers, true));
  }

  if (params.filters?.equipment?.other?.backstageArea) {
    where.push(eq(location.backstageArea, true));
  }

  if (params.filters?.equipment?.other?.danceFloor) {
    where.push(eq(location.danceFloor, true));
  }

  return where;
};

const LOCATION_COUNT_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
let cachedLocationCount: { count: number; timestamp: number } | null = null;

export const getTotalNumberOfLocations = createServerFn({
  method: "GET",
}).handler(async () => {
  const now = Date.now();
  if (
    cachedLocationCount !== null &&
    now - cachedLocationCount.timestamp < LOCATION_COUNT_CACHE_TTL_MS
  ) {
    console.log("Serving location count from cache.");
    return cachedLocationCount.count;
  }
  if (
    cachedLocationCount !== null &&
    now - cachedLocationCount.timestamp >= LOCATION_COUNT_CACHE_TTL_MS
  ) {
    cachedLocationCount = null;
  }

  console.log("Cache miss. Fetching location count from database...");
  // Only count published locations (exclude drafts)
  const query = db
    .select({ count: count() })
    .from(location)
    .where(
      and(
        eq(location.deleted, 0),
        eq(location.hidden, 0),
        eq(location.status, "published"),
      ),
    );
  const [data] = await query.execute();

  cachedLocationCount = { count: data.count, timestamp: Date.now() };

  return cachedLocationCount.count;
});

export const invalidateLocationCountCache = () => {
  cachedLocationCount = null;
};
